import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipProps {
  content: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  children: React.ReactElement;
  delay?: number; // Delay in milliseconds before showing tooltip
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** When true, popup allows pointer events (e.g. clickable buttons) */
  interactive?: boolean;
  /** 'hover' = show on mouse enter, 'contextmenu' = show on contextmenu */
  trigger?: 'hover' | 'contextmenu';
  /** Callback when visibility changes */
  onVisibleChange?: (isVisible: boolean) => void;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  delay = 200, // Default 200ms delay (much faster than browser default ~1000ms)
  position = 'top',
  interactive = false,
  trigger = 'hover',
  onVisibleChange,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const showTooltip = (e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    targetRef.current = target;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const show = () => {
      const rect = target.getBoundingClientRect();
      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
        case 'top-right':
          top = rect.top - 8;
          left = rect.right;
          break;
        case 'top-left':
          top = rect.top - 8;
          left = rect.left;
          break;
        case 'bottom':
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2;
          break;
        case 'bottom-right':
          top = rect.bottom + 8;
          left = rect.right;
          break;
        case 'bottom-left':
          top = rect.bottom + 8;
          left = rect.left;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 8;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
      }

      setTooltipPosition({ top, left });
      setIsVisible(true);
      onVisibleChange?.(true);
    };

    if (trigger === 'contextmenu') {
      show();
    } else {
      timeoutRef.current = setTimeout(show, delay);
    }
  };

  const hideTooltip = () => {
    // Clear the timeout if the mouse leaves before the delay completes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(false);
    onVisibleChange?.(false);
  };

  const scheduleHide = () => {
    if (!interactive) {
      hideTooltip();
      return;
    }
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(hideTooltip, 120);
  };

  const cancelScheduleHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // When trigger is click, close on outside mousedown
  useEffect(() => {
    if (trigger !== 'contextmenu' || !isVisible) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (targetRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      hideTooltip();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [trigger, isVisible]);

  // Clone the child element and add event handlers based on trigger
  const childWithHandlers = React.cloneElement(children, {
    onMouseEnter: trigger === 'hover' ? (e: React.MouseEvent) => {
      showTooltip(e);
      if (children.props.onMouseEnter) children.props.onMouseEnter(e);
    } : undefined,
    onMouseLeave: trigger === 'hover' ? (e: React.MouseEvent) => {
      scheduleHide();
      if (children.props.onMouseLeave) children.props.onMouseLeave(e);
    } : undefined,
    onContextMenu: trigger === 'contextmenu' ? (e: React.MouseEvent) => {
      e.preventDefault();
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip(e);
      }
      if (children.props.onContextMenu) children.props.onContextMenu(e);
    } : children.props.onContextMenu,
    onClick: trigger === 'contextmenu' ? (e: React.MouseEvent) => {
      e.preventDefault();
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip(e);
      }
      if (children.props.onClick) children.props.onClick(e);
    } : children.props.onClick,
  });

  return (
    <>
      {childWithHandlers}
      {isVisible && content != null && createPortal(
        <div
          ref={popupRef}
          className={`fixed px-bg-v2-lg py-bg-v2-md bg-bg-primary text-fg-primary text-xs rounded-bg-v2-md shadow-lg border border-border-primary whitespace-nowrap z-[9999] ${interactive || trigger === 'contextmenu' ? 'pointer-events-auto' : 'pointer-events-none'}`}
          style={{
            top: position === 'top' || position === 'top-right' || position === 'top-left'
              ? `${tooltipPosition.top}px`
              : position === 'bottom' || position === 'bottom-right' || position === 'bottom-left'
              ? `${tooltipPosition.top}px`
              : `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: position === 'top'
              ? 'translate(-50%, -100%)'
              : position === 'top-right'
              ? 'translate(-100%, -100%)'
              : position === 'top-left'
              ? 'translate(0, -100%)'
              : position === 'bottom'
              ? 'translate(-50%, 0)'
              : position === 'bottom-right'
              ? 'translate(-100%, 0)'
              : position === 'bottom-left'
              ? 'translate(0, 0)'
              : position === 'left'
              ? 'translate(-100%, -50%)'
              : 'translate(0, -50%)',
          }}
          onMouseEnter={interactive && trigger === 'hover' ? cancelScheduleHide : undefined}
          onMouseLeave={interactive && trigger === 'hover' ? hideTooltip : undefined}
        >
          {typeof content === 'function' ? content(hideTooltip) : content}
          {/* Arrow */}
          {(position === 'top' || position === 'bottom' || position === 'top-right' || position === 'top-left' || position === 'bottom-right' || position === 'bottom-left') && (
            <div
              className="absolute w-2 h-2 bg-bg-primary transform rotate-45"
              style={{
                [position === 'top' || position === 'top-right' || position === 'top-left' ? 'bottom' : 'top']: '-4px',
                left: position === 'top' || position === 'bottom' 
                  ? '50%' 
                  : position === 'top-right' || position === 'bottom-right'
                  ? 'calc(100% - 12px)'
                  : '12px',
                transform: (position === 'top' || position === 'bottom') 
                  ? 'translateX(-50%) rotate(45deg)' 
                  : 'rotate(45deg)',
              }}
            />
          )}
          {(position === 'left' || position === 'right') && (
            <div
              className="absolute w-2 h-2 bg-bg-primary transform rotate-45"
              style={{
                [position === 'left' ? 'right' : 'left']: '-4px',
                top: '50%',
                transform: 'translateY(-50%) rotate(45deg)',
              }}
            />
          )}
        </div>,
        document.body
      )}
    </>
  );
};

